import { createReactBlockSpec } from '@blocknote/react';

/**
 * Phrase reference ("section variant") block.
 *
 * Authoring/preview surfaces (e.g. the admin template library, which fetches unhydrated template
 * content) show template content as-authored, so a `phrase` placeholder block reaches the editor
 * directly. BlockNote builds its document against the schema and throws on any block type it does
 * not know (`Cannot read properties of undefined (reading 'isInGroup')`) — a single unknown block
 * would otherwise hard-crash the whole note editor. Registering this spec lets a phrase reference
 * render as a non-editable chip instead.
 *
 * In the note-creation flow phrase blocks are expanded server-side (see the notes hydration
 * service) and never reach the editor, so this spec is primarily an authoring/preview affordance
 * and a resilience guard.
 *
 * `content: 'none'` (the placeholder carries no inline content) + `meta.selectable: false` follow
 * the same constraints as the horizontalRule spec to avoid the read-only selection-plugin crash.
 */
export const phraseBlockSpec = createReactBlockSpec(
  {
    type: 'phrase',
    propSchema: {
      phraseId: { default: '' },
      phraseName: { default: '' },
      includeInForm: { default: true },
      required: { default: false },
      requiredMessage: { default: '' },
      requiredMode: { default: '' },
      readModeInline: { default: false },
    },
    content: 'none',
  },
  {
    meta: { selectable: false },
    render: (props) => {
      const phraseName = props.block?.props?.phraseName?.trim();
      const phraseId = props.block?.props?.phraseId ?? '';
      const label = phraseName || 'Section variant';

      return (
        <div contentEditable={false} className="my-1">
          <span
            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            data-testid="phrase-block-placeholder"
            data-phrase-id={phraseId}
            title={phraseId}
          >
            {label}
          </span>
        </div>
      );
    },
  },
)();
